import React from 'react';
import { generateProfileName } from '@/utils/profileNameGenerator';
import { calculateBehavioralScores } from '@/utils/scoreCalculations';
import { DCTOS_COLORS } from '@/lib/dctos-config';

interface SurveySubmission {
  id: string;
  full_name: string;
  email: string;
  profile_name?: string | null;
  scores: any;
  selected_words: any;
  selected_words_text: any;
  completed_at: string;
  created_at: string;
  assessment_type?: string;
  assessment_metadata?: {
    language?: string;
    duration_seconds?: number;
    [key: string]: any;
  };
}

interface TraitInsight {
  strength?: string;
  challenge?: string;
  modification?: string;
}

interface AIInsightsResponse {
  trait_insights?: TraitInsight[];
  selfInsights?: {
    trait_insights?: TraitInsight[];
    summary_points?: string[];
  };
  workInsights?: {
    trait_insights?: TraitInsight[];
    summary_points?: string[];
  };
  summary_points?: string[];
}

interface CognitiveData {
  overall_percentile: number;
  verbal_score: number;
  numerical_score: number;
  abstract_score: number;
}

interface PrintableProfileContentProps {
  submission: SurveySubmission;
  aiInsights: AIInsightsResponse | null;
  cognitiveData?: CognitiveData | null;
  showContactInfo?: boolean;
}

interface ChartDataItem {
  trait: string;
  value: number;
  color: string;
}

const getLanguageName = (code?: string | null): string => {
  if (!code || code === 'en') return 'English';
  if (code === 'pt') return 'Português';
  if (code === 'es') return 'Español';
  return 'English';
};

const formatDuration = (seconds?: number): string => {
  if (!seconds) return 'N/A';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes === 0) return `${remainingSeconds}s`;
  return `${minutes}m ${remainingSeconds}s`;
};

const TRAIT_LABELS: Record<string, { letter: string; name: string }> = {
  D: { letter: 'D', name: 'Drive' },
  C: { letter: 'C', name: 'Connection' },
  T: { letter: 'T', name: 'Task' },
  O: { letter: 'O', name: 'Order' },
  S: { letter: 'S', name: 'Style' },
};

// Compact PDF dot chart - optimized for 1-page fit
const PDFDotChart: React.FC<{ data: ChartDataItem[]; title: string }> = ({ data, title }) => {
  const width = 740;
  const height = 180;
  const padding = { left: 50, right: 30, top: 16, bottom: 24 };
  const chartWidth = width - padding.left - padding.right;
  const dotRadius = 7;
  
  const mainTraits = data.filter(d => d.trait !== 'S');
  const objectivityTrait = data.find(d => d.trait === 'S');
  
  const rowHeight = 24;
  const startY = padding.top + 10;
  
  const getX = (value: number) => padding.left + (value / 100) * chartWidth;
  
  return (
    <div style={{ marginBottom: '6px', flex: 1 }}>
      <div style={{ 
        fontSize: '11px', 
        fontWeight: 600, 
        marginBottom: '10px',
        color: '#1f2937',
        textAlign: 'center'
      }}>
        {title}
      </div>
      <svg width={width} height={height} style={{ display: 'block' }}>
        {/* White background */}
        <rect x={0} y={0} width={width} height={height} fill="white" rx={6} stroke="#e5e7eb" strokeWidth={1} />
        
        {/* 50% reference line */}
        <line
          x1={getX(50)}
          y1={padding.top}
          x2={getX(50)}
          y2={height - padding.bottom - 8}
          stroke="#e5e7eb"
          strokeWidth={1}
          strokeDasharray="3,3"
        />
        
        {/* Percentage labels */}
        {[0, 50, 100].map(pct => (
          <text
            key={pct}
            x={getX(pct)}
            y={height - 5}
            textAnchor="middle"
            fontSize={8}
            fill="#9ca3af"
          >
            {pct}%
          </text>
        ))}
        
        {/* Main traits (D, C, T, O) */}
        {mainTraits.length > 0 && (
          <>
            <polyline
              points={mainTraits.map((d, i) => `${getX(d.value)},${startY + i * rowHeight}`).join(' ')}
              fill="none"
              stroke="#d1d5db"
              strokeWidth={1.5}
            />
            
            {mainTraits.map((d, i) => {
              const x = getX(d.value);
              const y = startY + i * rowHeight;
              return (
                <g key={d.trait}>
                  <text x={padding.left - 20} y={y + 3} fontSize={9} fontWeight={600} fill="#374151">
                    {TRAIT_LABELS[d.trait]?.letter || d.trait}
                  </text>
                  <line x1={padding.left} x2={padding.left + chartWidth} y1={y} y2={y} stroke="#f3f4f6" strokeWidth={1} />
                  <circle cx={x} cy={y} r={dotRadius + 1.5} fill="white" stroke={d.color} strokeWidth={1.5} />
                  <circle cx={x} cy={y} r={dotRadius - 1.5} fill={d.color} />
                  <text x={padding.left + chartWidth + 6} y={y + 3} fontSize={8} fill="#6b7280">
                    {Math.round(d.value)}%
                  </text>
                </g>
              );
            })}
          </>
        )}
        
        {/* Style (S) */}
        {objectivityTrait && (
          <g>
            <line x1={padding.left} x2={padding.left + chartWidth} y1={height - padding.bottom - 2} y2={height - padding.bottom - 2} stroke="#f3f4f6" strokeWidth={1} />
            <text x={padding.left - 20} y={height - padding.bottom + 2} fontSize={9} fontWeight={600} fill="#374151">S</text>
            <circle cx={getX(objectivityTrait.value)} cy={height - padding.bottom - 2} r={dotRadius - 1} fill={objectivityTrait.color} />
            <text x={padding.left + chartWidth + 6} y={height - padding.bottom + 2} fontSize={8} fill="#6b7280">
              {Math.round(objectivityTrait.value)}%
            </text>
          </g>
        )}
      </svg>
    </div>
  );
};

const PrintableProfileContent: React.FC<PrintableProfileContentProps> = ({ submission, aiInsights, cognitiveData, showContactInfo = false }) => {
  const profileName = generateProfileName(submission);
  const assessmentLanguage = submission.assessment_metadata?.language;
  const assessmentDuration = submission.assessment_metadata?.duration_seconds;

  // Get display name based on showContactInfo setting
  const displayName = showContactInfo 
    ? submission.full_name 
    : submission.full_name.split(' ')[0]; // First name only

  const hasSelfBehavior = submission.scores?.selfBehavior;
  const hasWorkBehavior = submission.scores?.workBehavior;
  const isDualAssessment = hasSelfBehavior && hasWorkBehavior;

  const mapToChartData = (scores: any): ChartDataItem[] => {
    const calculated = calculateBehavioralScores(scores);
    return [
      { trait: 'D', value: calculated.aPercentile ?? 0, color: DCTOS_COLORS.drive },
      { trait: 'C', value: calculated.bPercentile ?? 0, color: DCTOS_COLORS.connection },
      { trait: 'T', value: calculated.cPercentile ?? 0, color: DCTOS_COLORS.task },
      { trait: 'O', value: calculated.dPercentile ?? 0, color: DCTOS_COLORS.order },
      { trait: 'S', value: calculated.ePercentile ?? 50, color: DCTOS_COLORS.style },
    ];
  };

  let selfChartData: ChartDataItem[] | null = null;
  let workChartData: ChartDataItem[] | null = null;

  if (isDualAssessment) {
    selfChartData = mapToChartData(submission.scores.selfBehavior);
    workChartData = mapToChartData(submission.scores.workBehavior);
  } else {
    const chartData = mapToChartData(submission.scores);
    if (submission.assessment_type === 'work_behavior') {
      workChartData = chartData;
    } else {
      selfChartData = chartData;
    }
  }

  // Limit insights to 3 per section for compact fit
  const maxInsights = 3;

  return (
    <div 
      id="printable-profile-content"
      style={{ 
        width: '800px', 
        backgroundColor: 'white', 
        fontFamily: 'system-ui, -apple-system, sans-serif',
        color: '#1f2937',
        padding: '20px 24px',
      }}
    >
      {/* Profile Info Card */}
      <div style={{ 
        padding: '16px 20px',
        marginBottom: '14px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: 700, margin: 0, color: '#111827', lineHeight: 1.2 }}>
              {displayName}
            </h1>
            {showContactInfo && (
              <p style={{ fontSize: '11px', color: '#6b7280', margin: '4px 0 0 0' }}>
                {submission.email}
              </p>
            )}
          </div>
          <span style={{ 
            fontSize: '14px',
            fontWeight: 700,
            color: '#2563eb',
            whiteSpace: 'nowrap',
            letterSpacing: '0.02em',
          }}>
            {profileName}
          </span>
        </div>
        
        {/* Only show metadata when showContactInfo is enabled */}
        {showContactInfo && (
          <div style={{ 
            display: 'flex', 
            gap: '20px', 
            marginTop: '10px',
            paddingTop: '8px',
            borderTop: '1px solid #e5e7eb',
            fontSize: '9px'
          }}>
            <div>
              <span style={{ color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Completed</span>
              <div style={{ fontWeight: 500, color: '#374151', marginTop: '1px' }}>
                {new Date(submission.completed_at).toLocaleDateString()}
              </div>
            </div>
            <div>
              <span style={{ color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Language</span>
              <div style={{ fontWeight: 500, color: '#374151', marginTop: '1px' }}>
                {getLanguageName(assessmentLanguage)}
              </div>
            </div>
            <div>
              <span style={{ color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Duration</span>
              <div style={{ fontWeight: 500, color: '#374151', marginTop: '1px' }}>
                {formatDuration(assessmentDuration)}
              </div>
            </div>
            <div>
              <span style={{ color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.3px' }}>Type</span>
              <div style={{ fontWeight: 500, color: '#374151', marginTop: '1px' }}>
                {isDualAssessment ? 'Full Assessment' : 'Single'}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Behavioral Charts - Side by side */}
      <div style={{ marginBottom: '14px' }}>
        <h3 style={{ 
          fontSize: '12px', 
          fontWeight: 600, 
          marginBottom: '8px',
          color: '#111827',
          borderBottom: '1px solid #e5e7eb',
          paddingBottom: '4px'
        }}>
          Behavioral Profile
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
          {selfChartData && (
            <PDFDotChart 
              data={selfChartData} 
              title={isDualAssessment ? "Natural Self" : "Behavioral Traits"} 
            />
          )}
          {workChartData && (
            <PDFDotChart 
              data={workChartData} 
              title="Work Persona" 
            />
          )}
        </div>
      </div>
      
      {/* Cognitive Assessment - Compact */}
      {cognitiveData && (
        <div style={{ marginBottom: '14px' }}>
          <h3 style={{ 
            fontSize: '12px', 
            fontWeight: 600, 
            marginBottom: '8px',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '4px'
          }}>
            Cognitive Assessment
          </h3>
          
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div style={{
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              padding: '16px 32px',
              textAlign: 'center',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#111827' }}>
                {cognitiveData.overall_percentile}%
              </div>
              <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>Overall Percentile</div>
            </div>
          </div>
        </div>
      )}
      
      {/* AI Insights - Compact */}
      {aiInsights && (
        <div style={{ marginBottom: '10px' }}>
          <h3 style={{ 
            fontSize: '12px', 
            fontWeight: 600, 
            marginBottom: '8px',
            color: '#111827',
            borderBottom: '1px solid #e5e7eb',
            paddingBottom: '4px'
          }}>
            AI Insights
          </h3>
          
          {isDualAssessment && aiInsights.selfInsights && aiInsights.workInsights ? (
            <div style={{ display: 'flex', gap: '12px' }}>
              {/* Self Insights */}
              <div style={{ flex: 1 }}>
                <h4 style={{ 
                  fontSize: '10px', 
                  fontWeight: 600, 
                  color: '#374151',
                  marginBottom: '6px',
                  backgroundColor: '#f0fdf4',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}>
                  🏠 Natural Self
                </h4>
                {(aiInsights.selfInsights.trait_insights || []).slice(0, maxInsights).map((insight, index) => (
                  <div key={index} style={{ 
                    marginBottom: '4px',
                    padding: '6px',
                    backgroundColor: '#fafafa',
                    borderRadius: '4px',
                    borderLeft: '2px solid #10b981',
                    fontSize: '9px',
                    lineHeight: '1.4',
                    color: '#374151'
                  }}>
                    {insight.strength} {insight.challenge}
                  </div>
                ))}
              </div>
              
              {/* Work Insights */}
              <div style={{ flex: 1 }}>
                <h4 style={{ 
                  fontSize: '10px', 
                  fontWeight: 600, 
                  color: '#374151',
                  marginBottom: '6px',
                  backgroundColor: '#eff6ff',
                  padding: '4px 8px',
                  borderRadius: '4px'
                }}>
                  💼 Work Persona
                </h4>
                {(aiInsights.workInsights.trait_insights || []).slice(0, maxInsights).map((insight, index) => (
                  <div key={index} style={{ 
                    marginBottom: '4px',
                    padding: '6px',
                    backgroundColor: '#fafafa',
                    borderRadius: '4px',
                    borderLeft: '2px solid #3b82f6',
                    fontSize: '9px',
                    lineHeight: '1.4',
                    color: '#374151'
                  }}>
                    {insight.modification}
                  </div>
                ))}
              </div>
            </div>
          ) : (aiInsights.trait_insights || []).length > 0 ? (
            <div>
              {aiInsights.trait_insights!.slice(0, maxInsights).map((insight, index) => (
                <div key={index} style={{ 
                  marginBottom: '4px',
                  padding: '6px 8px',
                  backgroundColor: '#fafafa',
                  borderRadius: '4px',
                  borderLeft: '2px solid #10b981',
                  fontSize: '9px',
                  lineHeight: '1.4',
                  color: '#374151'
                }}>
                  {insight.strength} {insight.challenge}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}
      
      {/* Footer */}
      <div style={{ 
        borderTop: '1px solid #e5e7eb', 
        paddingTop: '8px',
        textAlign: 'center'
      }}>
        <p style={{ fontSize: '8px', color: '#9ca3af', margin: 0 }}>
          © {new Date().getFullYear()} Zentrix Insights • Confidential
        </p>
      </div>
    </div>
  );
};

export default PrintableProfileContent;
